import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, CheckCircle2, Clock, CreditCard, ExternalLink, FileText, Landmark, TriangleAlert } from 'lucide-react';
import {
  BILLING_COUNTRIES,
  EMPTY_BILLING_DETAILS,
  billingDetailsErrors,
  normalizeTaxId,
  taxIdLabel,
  taxIdTypeFor,
  type BillingDetails,
  type BillingInterval,
} from '@vidmakr/shared';
import { api } from '@/lib/api';
import { reducers } from '@/lib/stdb';
import { errorMessage, toast } from '@/lib/toast';
import { formatMoney, goToStripe } from '@/lib/billing';
import { useSession } from '@/lib/session';
import { Badge, Button, Card, Field, Input, Modal, Select, Textarea, cx } from './ui';

export interface BillingDetailsState {
  details: BillingDetails | null;
  taxId: { value: string; status: string } | null;
  invoicing: { allowed: boolean; days: number; requested: boolean };
  payByInvoice: boolean;
}

/** An organization's company details and invoicing state (owners only). */
export function useBillingDetails(orgId: string | undefined, enabled: boolean) {
  const [state, setState] = useState<BillingDetailsState | null>(null);
  const [error, setError] = useState('');
  const reload = useCallback(async () => {
    if (!orgId || !enabled) return;
    try {
      setState(await api<BillingDetailsState>(`/api/billing/details?orgId=${encodeURIComponent(orgId)}`));
      setError('');
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [orgId, enabled]);
  useEffect(() => void reload(), [reload]);
  return { state, error, reload, setState };
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' });
  } catch {
    return null;
  }
})();
const countryName = (code: string) => regionNames?.of(code) ?? code;

function TaxIdBadge({ status }: { status: string }) {
  if (status === 'verified')
    return (
      <Badge tone="ok">
        <CheckCircle2 size={11} /> Verified
      </Badge>
    );
  if (status === 'pending')
    return (
      <Badge tone="neutral">
        <Clock size={11} /> Being checked
      </Badge>
    );
  if (status === 'unverified')
    return (
      <Badge tone="warn">
        <TriangleAlert size={11} /> Could not be verified
      </Badge>
    );
  return null;
}

/** Company details, tax number and invoicing for an organization (billing page). */
export function CompanyDetailsCard({ orgId, details }: { orgId: string; details: ReturnType<typeof useBillingDetails> }) {
  const { state, error, setState } = details;
  const [editing, setEditing] = useState(false);
  if (error) return <Card className="p-5 text-sm text-danger">{error}</Card>;
  if (!state) return null;
  const d = state.details;
  const complete = !!d && !Object.keys(billingDetailsErrors({ ...d, taxId: '' })).length;
  return (
    <Card className="p-5" data-company-details>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Building2 size={15} className="text-muted" />
        <h3 className="font-semibold">Company details</h3>
        {complete ? null : <Badge tone="warn">Needed for invoices</Badge>}
        <div className="flex-1" />
        {complete && !editing ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-edit-details>
            Edit
          </Button>
        ) : null}
      </div>
      <p className="mb-4 text-sm text-muted">Shown on every invoice and receipt. Add your VAT number so it appears too.</p>
      {complete && !editing ? (
        <DetailsSummary details={d!} taxStatus={state.taxId?.status} />
      ) : (
        <DetailsForm
          orgId={orgId}
          initial={d}
          taxStatus={state.taxId?.status}
          onCancel={complete ? () => setEditing(false) : undefined}
          onSaved={(s) => {
            setState({ ...state, ...s });
            setEditing(false);
          }}
        />
      )}
    </Card>
  );
}

function DetailsSummary({ details: d, taxStatus }: { details: BillingDetails; taxStatus?: string }) {
  return (
    <div className="grid gap-4 text-sm sm:grid-cols-2" data-details-summary>
      <div>
        <div className="font-medium">{d.companyName}</div>
        <div className="text-muted">
          {d.address.line1}
          {d.address.line2 ? `, ${d.address.line2}` : ''}
          <br />
          {[d.address.postalCode, d.address.city].filter(Boolean).join(' ')}
          {d.address.state ? `, ${d.address.state}` : ''}
          <br />
          {countryName(d.address.country)}
        </div>
      </div>
      <div className="space-y-1.5">
        <div>
          <span className="text-muted">Invoices to </span>
          {d.email}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-muted">{taxIdLabel(d.address.country)}</span>
          {d.taxId ? (
            <>
              <span className="font-mono text-[13px]" data-tax-id>
                {d.taxId}
              </span>
              {taxStatus ? <TaxIdBadge status={taxStatus} /> : null}
            </>
          ) : (
            <span className="text-faint">none</span>
          )}
        </div>
        {d.poNumber ? (
          <div>
            <span className="text-muted">PO number </span>
            {d.poNumber}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The company details form. Validates as you go with the same rules as the server. */
function DetailsForm({
  orgId,
  initial,
  taxStatus,
  onSaved,
  onCancel,
  submitLabel = 'Save details',
}: {
  orgId: string;
  initial: BillingDetails | null;
  taxStatus?: string;
  onSaved: (s: Pick<BillingDetailsState, 'details' | 'taxId'>) => void;
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const user = useSession((s) => s.user);
  const [d, setD] = useState<BillingDetails>(() => initial ?? { ...EMPTY_BILLING_DETAILS, email: user?.email ?? '' });
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const errors = useMemo(() => billingDetailsErrors(d), [d]);
  const err = (k: keyof typeof errors) => (touched ? errors[k] : undefined);
  const set = (patch: Partial<BillingDetails>) => setD((x) => ({ ...x, ...patch }));
  const setAddr = (patch: Partial<BillingDetails['address']>) => setD((x) => ({ ...x, address: { ...x.address, ...patch } }));
  const country = d.address.country;
  const taxSupported = !country || !!taxIdTypeFor(country);
  const save = async () => {
    setTouched(true);
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      const res = await api<Pick<BillingDetailsState, 'details' | 'taxId'>>('/api/billing/details', { method: 'PUT', json: { orgId, details: d } });
      toast.success('Company details saved');
      onSaved(res);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  const hint = (msg?: string) => (msg ? <span className="mt-1 block text-[11px] text-danger">{msg}</span> : null);
  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-details-form
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Legal company name">
          <Input value={d.companyName} onChange={(e) => set({ companyName: e.target.value })} autoComplete="organization" name="companyName" />
          {hint(err('companyName'))}
        </Field>
        <Field label="Email for invoices">
          <Input type="email" value={d.email} onChange={(e) => set({ email: e.target.value })} autoComplete="email" name="billingEmail" />
          {hint(err('email'))}
        </Field>
      </div>
      <Field label="Address">
        <Input value={d.address.line1} onChange={(e) => setAddr({ line1: e.target.value })} placeholder="Street and number" autoComplete="address-line1" name="line1" />
        {hint(err('line1'))}
      </Field>
      <Input value={d.address.line2} onChange={(e) => setAddr({ line2: e.target.value })} placeholder="Floor, suite (optional)" autoComplete="address-line2" name="line2" aria-label="Address line 2" />
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Postal code">
          <Input value={d.address.postalCode} onChange={(e) => setAddr({ postalCode: e.target.value })} autoComplete="postal-code" name="postalCode" />
          {hint(err('postalCode'))}
        </Field>
        <Field label="City">
          <Input value={d.address.city} onChange={(e) => setAddr({ city: e.target.value })} autoComplete="address-level2" name="city" />
          {hint(err('city'))}
        </Field>
        <Field label="State / region (optional)">
          <Input value={d.address.state} onChange={(e) => setAddr({ state: e.target.value })} autoComplete="address-level1" name="state" />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Country">
          <Select
            className="w-full"
            value={country}
            onChange={(e) => setD((x) => ({ ...x, address: { ...x.address, country: e.target.value }, taxId: normalizeTaxId(e.target.value, x.taxId) }))}
            name="country"
          >
            <option value="">Choose…</option>
            {BILLING_COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {countryName(c)}
              </option>
            ))}
          </Select>
          {hint(err('country'))}
        </Field>
        <Field label={<span className="inline-flex items-center gap-1.5">{country ? taxIdLabel(country) : 'VAT / tax number'} (optional) {taxStatus && d.taxId === initial?.taxId ? <TaxIdBadge status={taxStatus} /> : null}</span>}>
          <Input
            value={d.taxId}
            onChange={(e) => set({ taxId: e.target.value })}
            onBlur={() => set({ taxId: normalizeTaxId(country, d.taxId) })}
            placeholder={country && taxIdTypeFor(country) === 'eu_vat' ? `${country === 'GR' ? 'EL' : country}123456789` : country === 'NO' ? 'NO123456789MVA' : ''}
            disabled={!taxSupported}
            name="taxId"
          />
          {taxSupported ? hint(err('taxId')) : <span className="mt-1 block text-[11px] text-faint">Tax numbers from this country cannot be added to invoices yet.</span>}
        </Field>
      </div>
      <Field label="Purchase order number (optional)" hint="Printed on invoices">
        <Input value={d.poNumber} onChange={(e) => set({ poNumber: e.target.value })} name="poNumber" />
        {hint(err('poNumber'))}
      </Field>
      <div className="flex justify-end gap-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit" variant="primary" loading={saving} data-save-details>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

interface Invoice {
  id: string;
  number: string;
  created: number;
  dueDate: number;
  total: number;
  amountDue: number;
  currency: string;
  status: string;
  url: string;
  pdf: string;
}

/** The organization's invoices from Stripe. */
export function InvoicesCard({ orgId, refreshKey }: { orgId: string; refreshKey: unknown }) {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  useEffect(() => {
    let live = true;
    api<{ invoices: Invoice[] }>(`/api/billing/invoices?orgId=${encodeURIComponent(orgId)}`)
      .then((r) => live && setInvoices(r.invoices))
      .catch(() => live && setInvoices([]));
    return () => {
      live = false;
    };
  }, [orgId, refreshKey]);
  if (!invoices?.length) return null;
  const date = (s: number) => (s ? new Date(s * 1000).toLocaleDateString() : '');
  const overdue = (i: Invoice) => i.status === 'open' && i.dueDate && i.dueDate * 1000 < Date.now();
  return (
    <Card className="p-5" data-invoices>
      <div className="mb-3 flex items-center gap-2">
        <FileText size={15} className="text-muted" />
        <h3 className="font-semibold">Invoices</h3>
      </div>
      <div className="divide-y divide-line text-sm">
        {invoices.map((i) => (
          <div key={i.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2" data-invoice={i.number || i.id}>
            <span className="w-28 font-mono text-[13px]">{i.number || '—'}</span>
            <span className="w-24 text-muted">{date(i.created)}</span>
            <span className="w-24 tabular-nums">{formatMoney(i.total, i.currency)}</span>
            <span className="flex-1">
              {i.status === 'paid' ? (
                <Badge tone="ok">Paid</Badge>
              ) : i.status === 'open' ? (
                <Badge tone={overdue(i) ? 'danger' : 'warn'}>
                  {overdue(i) ? 'Overdue' : 'Open'}
                  {i.dueDate ? ` · due ${date(i.dueDate)}` : ''}
                </Badge>
              ) : (
                <Badge>{i.status === 'void' ? 'Void' : i.status === 'uncollectible' ? 'Uncollectible' : i.status}</Badge>
              )}
            </span>
            <span className="flex gap-3 text-xs">
              {i.url ? (
                <a className="inline-flex items-center gap-1 text-accent-2 hover:underline" href={i.url} target="_blank" rel="noreferrer">
                  {i.status === 'open' ? 'Pay' : 'View'} <ExternalLink size={11} />
                </a>
              ) : null}
              {i.pdf ? (
                <a className="text-muted hover:text-fg hover:underline" href={i.pdf} target="_blank" rel="noreferrer">
                  PDF
                </a>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Signing up for Enterprise: seats, then company details, then how to pay —
 * by card (Stripe Checkout) or by invoice when a superadmin has allowed it
 * (otherwise the owner can ask for it).
 */
export function EnterpriseDialog({
  open,
  onClose,
  orgId,
  details,
  interval,
  seats,
  pricePerSeat,
  currency,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  details: ReturnType<typeof useBillingDetails>;
  interval: BillingInterval;
  seats: number;
  pricePerSeat: number;
  currency: string;
  onDone: () => void;
}) {
  const { state, setState } = details;
  const [pay, setPay] = useState<'invoice' | 'card'>('card');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (state?.invoicing.allowed) setPay('invoice');
  }, [state?.invoicing.allowed]);
  if (!state) return null;
  const d = state.details;
  const complete = !!d && !Object.keys(billingDetailsErrors({ ...d, taxId: '' })).length;
  const inv = state.invoicing;
  const total = formatMoney(pricePerSeat * seats * (interval === 'year' ? 12 : 1), currency);
  const subscribeByInvoice = async () => {
    setBusy(true);
    try {
      await api('/api/billing/invoice-subscribe', { method: 'POST', json: { orgId, plan: 'enterprise', interval, seats } });
      toast.success(`Enterprise is active. The first invoice goes to ${d?.email}.`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const requestInvoicing = async () => {
    setBusy(true);
    try {
      await reducers().requestInvoicing({ orgId, note });
      setState({ ...state, invoicing: { ...inv, requested: true } });
      toast.success('Request sent. We will email you when invoicing is on.');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="Set up Enterprise" width={640}>
      <div className="space-y-5" data-enterprise-dialog>
        <div className="rounded-xl border border-line bg-panel-2 p-3 text-sm">
          <b>{seats} editor seats</b>, billed {interval === 'year' ? 'yearly' : 'monthly'}: {total} per {interval === 'year' ? 'year' : 'month'}
          <span className="text-muted"> (before tax)</span>
        </div>

        <section>
          <h3 className="mb-2 text-sm font-semibold">1. Company details</h3>
          {complete && !editing ? (
            <div className="rounded-xl border border-line p-3">
              <DetailsSummary details={d!} taxStatus={state.taxId?.status} />
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => setEditing(true)}>
                Edit
              </Button>
            </div>
          ) : (
            <DetailsForm
              orgId={orgId}
              initial={d}
              taxStatus={state.taxId?.status}
              submitLabel="Save and continue"
              onCancel={complete ? () => setEditing(false) : undefined}
              onSaved={(s) => {
                setState({ ...state, ...s });
                setEditing(false);
              }}
            />
          )}
        </section>

        <section className={cx(!complete && 'pointer-events-none opacity-50')}>
          <h3 className="mb-2 text-sm font-semibold">2. How do you want to pay?</h3>
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Payment">
            {(
              [
                { id: 'invoice', icon: <Landmark size={16} />, title: 'By invoice', text: inv.allowed ? `Bank transfer, due ${inv.days} days after each invoice.` : 'Bank transfer on payment terms, once we have approved your company.' },
                { id: 'card', icon: <CreditCard size={16} />, title: 'By card', text: 'Charged automatically; receipts and invoices by email.' },
              ] as const
            ).map((o) => (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={pay === o.id}
                data-pay={o.id}
                onClick={() => setPay(o.id)}
                className={cx('rounded-xl border p-3 text-left', pay === o.id ? 'border-accent bg-accent/10' : 'border-line hover:bg-panel-2')}
              >
                <div className="mb-1 flex items-center gap-2 font-medium">
                  {o.icon} {o.title}
                </div>
                <div className="text-xs text-muted">{o.text}</div>
              </button>
            ))}
          </div>
          <div className="mt-4">
            {pay === 'card' ? (
              <Button variant="primary" disabled={!complete || busy} onClick={() => void goToStripe('/api/billing/checkout', { orgId, plan: 'enterprise', interval, seats })} data-enterprise-card>
                <CreditCard size={14} /> Continue to payment
              </Button>
            ) : inv.allowed ? (
              <div className="space-y-2">
                <p className="text-xs text-muted">Enterprise starts right away. Stripe emails each invoice to {d?.email ?? 'your billing email'}, with your company details{d?.taxId ? ' and VAT number' : ''}.</p>
                <Button variant="primary" disabled={!complete} loading={busy} onClick={() => void subscribeByInvoice()} data-enterprise-invoice>
                  <FileText size={14} /> Start Enterprise, pay by invoice
                </Button>
              </div>
            ) : inv.requested ? (
              <p className="flex items-center gap-2 text-sm text-muted" data-invoicing-requested>
                <Clock size={14} /> Requested. We will email the owners when you can pay by invoice; until then you can start with a card and switch later.
              </p>
            ) : (
              <div className="space-y-2">
                <Textarea rows={2} placeholder="Anything we should know? (expected seats, procurement process…)" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button disabled={!complete} loading={busy} onClick={() => void requestInvoicing()} data-request-invoicing>
                  Ask to pay by invoice
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}
